
import React from 'react';
import { getSmartEmail } from '@/utils/smartEmailUtils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Star, ExternalLink, Trash2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog';

interface Lead {
  id: string;
  name: string;
  category?: string | null;
  address?: string | null;
  phone?: string | null;
  website?: string | null;
  rating?: number | null;
  [key: string]: any;
}

interface SharedLeadsTableProps {
  leads: Lead[];
  onRowClick?: (leadId: string) => void;
  clickable?: boolean;
  extraColumns?: { header: string; render: (lead: Lead) => React.ReactNode }[];
  selectedLeadIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  keyContactsMap?: Map<string, any[]>;
  onRemoveLead?: (lead: Lead) => void;
  canRemove?: (lead: Lead) => boolean;
}

const SharedLeadsTable: React.FC<SharedLeadsTableProps> = ({
  leads,
  onRowClick,
  clickable = true,
  extraColumns = [],
  selectedLeadIds,
  onSelectionChange,
  keyContactsMap,
  onRemoveLead,
  canRemove,
}) => {
  const selectable = !!selectedLeadIds && !!onSelectionChange;
  const allSelected = selectable && leads.length > 0 && leads.every(l => selectedLeadIds!.has(l.id));
  const someSelected = selectable && leads.some(l => selectedLeadIds!.has(l.id)) && !allSelected;

  const toggleAll = () => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(leads.map(l => l.id)));
    }
  };

  const toggleOne = (id: string) => {
    if (!onSelectionChange || !selectedLeadIds) return;
    const next = new Set(selectedLeadIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  return (
    <div className="overflow-x-auto rounded-md border border-border/60">
      <Table>
        <TableHeader>
          <TableRow>
            {selectable && (
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                  onCheckedChange={toggleAll}
                  aria-label="Select all"
                />
              </TableHead>
            )}
            <TableHead>Name</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Address</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Website</TableHead>
            <TableHead>Rating</TableHead>
            {extraColumns.map((col) => (
              <TableHead key={col.header}>{col.header}</TableHead>
            ))}
            {onRemoveLead && <TableHead className="w-10"></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {leads.map((lead) => {
            const smartResult = getSmartEmail(lead, keyContactsMap?.get(lead.id));
            const displayEmail = smartResult?.email || '—';

            return (
              <TableRow
                key={lead.id}
                className={clickable ? 'cursor-pointer' : ''}
                onClick={() => clickable && onRowClick?.(lead.id)}
                data-state={selectable && selectedLeadIds!.has(lead.id) ? 'selected' : undefined}
              >
                {selectable && (
                  <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedLeadIds!.has(lead.id)}
                      onCheckedChange={() => toggleOne(lead.id)}
                      aria-label={`Select ${lead.name}`}
                    />
                  </TableCell>
                )}
                <TableCell className="font-medium">
                  <div className="flex items-center gap-1.5">
                    <span className="text-foreground">{lead.name}</span>
                    {clickable && (
                      <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {lead.category ? (
                    <Badge variant="outline" className="text-xs">
                      {lead.category}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground max-w-[200px] truncate">
                  {lead.address || '—'}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {lead.phone || '—'}
                </TableCell>
                <TableCell className="text-muted-foreground max-w-[200px] truncate">
                  {displayEmail}
                </TableCell>
                <TableCell className="text-muted-foreground max-w-[180px] truncate">
                  {lead.website || '—'}
                </TableCell>
                <TableCell>
                  {lead.rating ? (
                    <div className="flex items-center gap-1">
                      <Star className="h-3.5 w-3.5 fill-current text-amber-500" />
                      <span className="text-sm">{lead.rating}</span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                {extraColumns.map((col) => (
                  <TableCell key={col.header}>{col.render(lead)}</TableCell>
                ))}
                {onRemoveLead && (
                  <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                    {canRemove?.(lead) && (
                      <DeleteConfirmationDialog
                        entityName={lead.name}
                        onConfirm={() => onRemoveLead(lead)}
                        trigger={
                          <button className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        }
                      />
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default SharedLeadsTable;
